import React, { useState, useEffect, useRef, useCallback } from 'react'
import Prompt from '@/v2/components/common/Prompt'
import { useQueryClient } from '@tanstack/react-query'
import Button from '@/v2/components/common/Button'
import { TextArea, InlineButtons } from './styles'
import { useGetDoctors } from '@/v2/hooks/useDoctors.js'
import Typography from '@/v2/components/common/Typography'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import { baseURL, headers } from '@/v2/constants/requests'

const NoteForm = ({
  showAlertBeforeLeaving = false,
  note,
  appointmentId,
  patientId,
  onSave,
  onEdit,
  confirmButtonStyle = 'contained',
  onCancel,
}) => {
  const ref = useRef(null)
  const debounceRef = useRef(null)
  const isMountedRef = useRef(true)
  const abortControllerRef = useRef(null)
  const queryClient = useQueryClient()
  const { data: doctors, isLoading: doctorsLoading } = useGetDoctors()
  const { onOpenSnackbar } = useSnackbarContext()

  const createNote = useCallback(
    async ({ doctorId, annotation }) => {
      if (!isMountedRef.current) {
        throw new Error('Component unmounted')
      }

      const response = await fetch(
        `${baseURL}/v2/rest/patients/${patientId}/treatments/appointments/${appointmentId}/notes`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ doctor_id: doctorId, annotation }),
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to create note')
      }

      const result = await response.json()

      if (isMountedRef.current) {
        queryClient.invalidateQueries({
          queryKey: ['Notes', `${appointmentId}`],
        })
        queryClient.setQueryData(['Treatments', `${patientId}`], old =>
          old
            ? old.map(item =>
                item.id === appointmentId && !!item.appointmentType
                  ? {
                      ...item,
                      notes: [...item.notes, result],
                    }
                  : item
              )
            : old
        )
      }

      return result
    },
    [patientId, appointmentId, queryClient]
  )

  const updateNote = useCallback(
    async ({ doctorId, annotation }) => {
      if (!isMountedRef.current) {
        throw new Error('Component unmounted')
      }

      const response = await fetch(
        `${baseURL}/v2/rest/patients/${patientId}/treatments/appointments/${appointmentId}/notes/${note?.id}`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify({ doctor_id: doctorId, annotation }),
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to update note')
      }

      const result = await response.json()

      if (isMountedRef.current) {
        queryClient.invalidateQueries({
          queryKey: ['Notes', `${appointmentId}`],
        })
        queryClient.setQueryData(['Treatments', `${patientId}`], old =>
          old
            ? old.map(item =>
                item.id === appointmentId && !!item.appointmentType
                  ? {
                      ...item,
                      notes: item.notes.map(note =>
                        note.id === note?.id ? { ...note, ...result } : note
                      ),
                    }
                  : item
              )
            : old
        )
      }

      return result
    },
    [patientId, appointmentId, note?.id, queryClient]
  )

  const [value, setValue] = useState('')
  const [doctor, setDoctor] = useState()
  const [lastSavedValue, setLastSavedValue] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  const safeSetValue = useCallback(newValue => {
    if (isMountedRef.current) {
      setValue(newValue)
    }
  }, [])

  const safeSetDoctor = useCallback(newDoctor => {
    if (isMountedRef.current) {
      setDoctor(newDoctor)
    }
  }, [])

  const safeSetLastSavedValue = useCallback(newValue => {
    if (isMountedRef.current) {
      setLastSavedValue(newValue)
    }
  }, [])

  const safeSetIsSaving = useCallback(newValue => {
    if (isMountedRef.current) {
      setIsSaving(newValue)
    }
  }, [])

  const safeOnOpenSnackbar = useCallback(
    (message, type) => {
      if (isMountedRef.current && onOpenSnackbar) {
        onOpenSnackbar(message, type)
      }
    },
    [onOpenSnackbar]
  )

  useEffect(() => {
    return () => {
      isMountedRef.current = false
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
        debounceRef.current = null
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  // TODO: remove setDoctor once doctor is not a required field on backend
  useEffect(() => {
    if (!isMountedRef.current) return

    if (!doctorsLoading && doctors?.length) safeSetDoctor(doctors[0])
    if (note) {
      safeSetValue(note.annotation)
      safeSetLastSavedValue(note.annotation)
      if (ref.current) {
        ref.current.value = note.annotation
      }
      note.doctor && safeSetDoctor(note.doctor)
    }
  }, [
    doctorsLoading,
    doctors,
    note,
    safeSetValue,
    safeSetDoctor,
    safeSetLastSavedValue,
  ])

  const autoSave = async content => {
    if (content === lastSavedValue || !content.trim()) {
      return
    }

    if (!doctor) {
      return
    }

    if (!isMountedRef.current) return

    safeSetIsSaving(true)

    abortControllerRef.current = new AbortController()

    try {
      if (note) {
        if (note.id === 0) {
          if (isMountedRef.current) {
            onEdit && onEdit({ ...note, annotation: content })
            safeSetLastSavedValue(content)
          }
        } else {
          const result = await updateNote({
            doctorId: doctor.id,
            annotation: content,
          })

          if (isMountedRef.current) {
            onEdit && onEdit(result)
            safeSetLastSavedValue(content)
          }
        }
      } else {
        const result = await createNote({
          doctorId: doctor.id,
          annotation: content,
        })

        if (isMountedRef.current) {
          onSave && onSave(result)
          safeSetLastSavedValue(content)
        }
      }
    } catch (e) {
      if (e.name === 'AbortError' || e.message === 'Component unmounted') {
        return
      }
      console.error('Autosave error:', e)
      safeOnOpenSnackbar('Error on autosave.', 'error')
    } finally {
      safeSetIsSaving(false)
    }
  }

  const handleSave = async () => {
    if (!isMountedRef.current) return

    try {
      if (note) {
        if (note.id === 0) {
          if (ref.current) {
            ref.current.value = ''
          }
          if (isMountedRef.current) {
            onEdit && onEdit({ ...note, annotation: value })
            safeOnOpenSnackbar('Edited note.')
            safeSetValue('')
            safeSetLastSavedValue('')
          }
        } else {
          const result = await updateNote({
            doctorId: doctor.id,
            annotation: value,
          })

          if (isMountedRef.current) {
            if (ref.current) {
              ref.current.value = ''
            }
            onEdit && onEdit(result)
            safeOnOpenSnackbar('Edited note.')
            safeSetValue('')
            safeSetLastSavedValue('')
          }
        }
      } else {
        const result = await createNote({
          doctorId: doctor.id,
          annotation: value,
        })

        if (isMountedRef.current) {
          if (ref.current) {
            ref.current.value = ''
          }
          onSave && onSave(result)
          safeOnOpenSnackbar('Created note.')
          safeSetValue('')
          safeSetLastSavedValue('')
        }
      }
    } catch (e) {
      if (e.message === 'Component unmounted') {
        return
      }
      console.error(e)
      safeOnOpenSnackbar('Error on save.', 'error')
    }
  }

  const handleChange = value => {
    if (!isMountedRef.current) return

    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    safeSetValue(value)

    debounceRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        autoSave(value)
      }
    }, 60000)
  }

  const handleBlur = () => {
    if (!isMountedRef.current) return

    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    if (isMountedRef.current) {
      autoSave(value)
    }
  }

  if (!doctorsLoading && (!doctors || doctors.length === 0))
    return (
      <Typography>Please, create a doctor to start creating notes.</Typography>
    )

  return (
    <>
      <Prompt
        when={showAlertBeforeLeaving && value !== ''}
        message="You have unsaved changes, are you sure you want to leave?"
      />
      <TextArea
        multiline
        className="note-input"
        inputRef={ref}
        rows={4}
        placeholder="Write a new note..."
        defaultValue=""
        onChange={({ target }) => handleChange(target.value)}
        onBlur={handleBlur}
      />
      <InlineButtons className="note-input-buttons">
        {onCancel && (
          <Button
            color="primary"
            variant="text"
            sx={{ fontSize: '14px' }}
            onClick={onCancel}
          >
            Cancel
          </Button>
        )}
        <Button
          color={confirmButtonStyle === 'contained' ? 'secondary' : 'primary'}
          variant={confirmButtonStyle}
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </InlineButtons>
    </>
  )
}

export default NoteForm
